export * from './ui';
export * from './chart';
export * from './watchlistGroup';
export * from './watchlistQuoteSort';

/** 个股状态（接口 stockState 字段） */
export const STOCK_STATE = Object.freeze({
  LISTED: 1,
  SUSPENDED: 2,
  DELISTED: 3
});

/**
 * 股票行情类型（拍平后的图表周期：分时、分钟线、日周月 K 同级互斥）
 * @readonly
 */
export const STOCK_QUOTE_TYPE = {
  TIME_SHARE: 0,
  MINUTE_1: 4,
  MINUTE_5: 5,
  MINUTE_15: 6,
  MINUTE_30: 7,
  MINUTE_60: 8,
  DAILY_K: 1,
  WEEKLY_K: 2,
  MONTHLY_K: 3,
  YEARLY_K: 9
};

/** 个股详情默认行情类型：1 分 */
export const DEFAULT_STOCK_QUOTE_TYPE = STOCK_QUOTE_TYPE.MINUTE_1;

/** 分钟线行情下拉选项（label 为 i18n key，不含「分时」） */
export const MINUTE_QUOTE_TYPE_OPTIONS = [
  { value: STOCK_QUOTE_TYPE.MINUTE_1, labelKey: 'selfSelected.tip.minute_1' },
  { value: STOCK_QUOTE_TYPE.MINUTE_5, labelKey: 'selfSelected.tip.minute_5' },
  { value: STOCK_QUOTE_TYPE.MINUTE_15, labelKey: 'selfSelected.tip.minute_15' },
  { value: STOCK_QUOTE_TYPE.MINUTE_30, labelKey: 'selfSelected.tip.minute_30' },
  { value: STOCK_QUOTE_TYPE.MINUTE_60, labelKey: 'selfSelected.tip.minute_60' }
];

/** K 线行情类型与 filterIndex 映射（0 日 / 1 周 / 2 月 / 3 年） */
export const KLINE_QUOTE_TYPE_OPTIONS = [
  { type: STOCK_QUOTE_TYPE.DAILY_K, filterIndex: 0, labelKey: 'selfSelected.tip.daily_k' },
  { type: STOCK_QUOTE_TYPE.WEEKLY_K, filterIndex: 1, labelKey: 'selfSelected.tip.weekly_k' },
  { type: STOCK_QUOTE_TYPE.MONTHLY_K, filterIndex: 2, labelKey: 'selfSelected.tip.monthly_k' },
  { type: STOCK_QUOTE_TYPE.YEARLY_K, filterIndex: 3, labelKey: 'selfSelected.tip.yearly_k' }
];

/**
 * 是否为分钟线行情类型。
 * @param {number} type - 行情类型
 * @returns {boolean}
 */
export const isMinuteQuoteType = (type) => {
  return (
    type === STOCK_QUOTE_TYPE.MINUTE_1 ||
    type === STOCK_QUOTE_TYPE.MINUTE_5 ||
    type === STOCK_QUOTE_TYPE.MINUTE_15 ||
    type === STOCK_QUOTE_TYPE.MINUTE_30 ||
    type === STOCK_QUOTE_TYPE.MINUTE_60
  );
};

/**
 * 是否为分时或分钟线行情（非日周月 K）。
 * @param {number} type - 行情类型
 * @returns {boolean}
 */
export const isIntradayQuoteType = (type) => {
  return type === STOCK_QUOTE_TYPE.TIME_SHARE || isMinuteQuoteType(type);
};

/**
 * 是否为日/周/月 K 行情类型。
 * @param {number} type - 行情类型
 * @returns {boolean}
 */
export const isKLineQuoteType = (type) => {
  return (
    type === STOCK_QUOTE_TYPE.DAILY_K ||
    type === STOCK_QUOTE_TYPE.WEEKLY_K ||
    type === STOCK_QUOTE_TYPE.MONTHLY_K ||
    type === STOCK_QUOTE_TYPE.YEARLY_K
  );
};

/**
 * 由 K 线行情类型获取 filterIndex（兼容仍使用 0/1/2/3 下标的页面）。
 * @param {number} type - 行情类型
 * @returns {number}
 */
export const getFilterIndexFromQuoteType = (type) => {
  const target = KLINE_QUOTE_TYPE_OPTIONS.find((item) => item.type === type);
  return target?.filterIndex ?? 0;
};

/**
 * 由 filterIndex 获取对应 K 线行情类型。
 * @param {number} filterIndex - 0 日K / 1 周K / 2 月K / 3 年K
 * @returns {number}
 */
export const getQuoteTypeFromFilterIndex = (filterIndex) => {
  const target = KLINE_QUOTE_TYPE_OPTIONS.find((item) => item.filterIndex === filterIndex);
  return target?.type ?? STOCK_QUOTE_TYPE.DAILY_K;
};

/**
 * 根据行情类型值获取 i18n key。
 * @param {number} type - 行情类型枚举值
 * @returns {string|undefined}
 */
export const getStockQuoteTypeLabelKey = (type) => {
  if (type === STOCK_QUOTE_TYPE.TIME_SHARE) {
    return 'selfSelected.tip.time_share';
  }
  const klineLabelKey = KLINE_QUOTE_TYPE_OPTIONS.find((item) => item.type === type)?.labelKey;
  if (klineLabelKey) {
    return klineLabelKey;
  }
  return MINUTE_QUOTE_TYPE_OPTIONS.find((item) => item.value === type)?.labelKey;
};

/**
 * 日/周/月/年 K → 交易日历、打点统计接口 dateType（1 日 / 2 周 / 3 月 / 4 年）。
 * @readonly
 */
export const DATE_TYPE_BY_QUOTE_TYPE = Object.freeze({
  [STOCK_QUOTE_TYPE.DAILY_K]: 1,
  [STOCK_QUOTE_TYPE.WEEKLY_K]: 2,
  [STOCK_QUOTE_TYPE.MONTHLY_K]: 3,
  [STOCK_QUOTE_TYPE.YEARLY_K]: 4
});

/**
 * 由行情类型获取接口 dateType。
 * @param {number} quoteType {@link STOCK_QUOTE_TYPE}
 * @returns {number}
 */
export const getDateTypeFromQuoteType = (quoteType) => {
  return DATE_TYPE_BY_QUOTE_TYPE[quoteType] ?? 1;
};

/**
 * K 周期 AI 复盘：日期格式、话术、接口 frequencyType（按 quoteType 建表）。
 * @readonly
 */
export const CHART_PERIOD_AI_REVIEW_CONFIG_BY_QUOTE_TYPE = Object.freeze({
  [STOCK_QUOTE_TYPE.DAILY_K]: { dateFormat: 'YYYY.MM.DD', periodLabel: '日线', frequencyType: 1 },
  [STOCK_QUOTE_TYPE.WEEKLY_K]: {
    dateFormat: 'YYYY.MM.DD',
    periodLabel: '周线',
    frequencyType: 2,
    alignIsoWeekStart: true
  },
  [STOCK_QUOTE_TYPE.MONTHLY_K]: { dateFormat: 'YYYY.MM', periodLabel: '月线', frequencyType: 3 },
  [STOCK_QUOTE_TYPE.YEARLY_K]: { dateFormat: 'YYYY', periodLabel: '年线', frequencyType: 4 }
});

/**
 * 按行情类型过滤时间范围下拉选项。
 * @param {Array<{ value: number, label: string }>} allList - 全量时间范围选项
 * @param {number} quoteType {@link STOCK_QUOTE_TYPE}
 * @returns {Array<{ value: number, label: string }>}
 */
export const resolveDateFilterListByQuoteType = (allList, quoteType) => {
  if (quoteType === STOCK_QUOTE_TYPE.WEEKLY_K) {
    return allList.filter((item) => item.value !== 0);
  }
  if (quoteType === STOCK_QUOTE_TYPE.MONTHLY_K) {
    return allList.filter((item) => ![0, 1, 2].includes(item.value));
  }
  if (quoteType === STOCK_QUOTE_TYPE.YEARLY_K) {
    return allList.filter((item) => item.value >= 4);
  }
  return allList;
};

/** 行情类型 → 分钟 K 线聚合周期（分钟数） */
export const PERIOD_MIN_BY_QUOTE_TYPE = Object.freeze({
  [STOCK_QUOTE_TYPE.MINUTE_1]: 1,
  [STOCK_QUOTE_TYPE.MINUTE_5]: 5,
  [STOCK_QUOTE_TYPE.MINUTE_15]: 15,
  [STOCK_QUOTE_TYPE.MINUTE_30]: 30,
  [STOCK_QUOTE_TYPE.MINUTE_60]: 60
});

/**
 * 由行情类型获取分钟 K 聚合周期。
 * @param {number} quoteType {@link STOCK_QUOTE_TYPE}
 * @returns {number}
 */
export const getPeriodMinFromQuoteType = (quoteType) => {
  return PERIOD_MIN_BY_QUOTE_TYPE[quoteType] ?? 1;
};

/** WS 推送的 `tradeStatus` 枚举：1-盘前 2-实时中 3-收盘 4-盘后 */
export const STOCK_TRADE_STATUS = Object.freeze({
  PRE: 1,
  TRADING: 2,
  CLOSING: 3,
  AFTER: 4
});

/** 分时图时段下拉选项（美股），value 沿用 {@link STOCK_TRADE_STATUS} 并作为接口 `tradeStatus` 入参 */
export const TIME_SHARE_SESSION_OPTIONS = [
  { value: STOCK_TRADE_STATUS.PRE, labelKey: 'selfSelected.tip.pre_market' },
  { value: STOCK_TRADE_STATUS.TRADING, labelKey: 'selfSelected.tip.intraday_trading' }
];

/**
 * 解析分时图默认时段：盘前维持盘前，盘中 / 收盘 / 盘后均落到盘中。
 * @param {number|null|undefined} tradeStatus {@link STOCK_TRADE_STATUS}
 * @returns {number} {@link STOCK_TRADE_STATUS} 中的 PRE 或 TRADING
 */
export const resolveDefaultTimeShareSession = (tradeStatus) => {
  return Number(tradeStatus) === STOCK_TRADE_STATUS.PRE ? STOCK_TRADE_STATUS.PRE : STOCK_TRADE_STATUS.TRADING;
};

/**
 * 解析个股研究复盘默认行情类型：A/港/美股默认分时，其他市场默认日 K。
 * @param {number} marketType - 1 A股 2 港股 3 美股 4 其他
 * @returns {number} {@link STOCK_QUOTE_TYPE}
 */
export const resolveDefaultStockQuoteType = (marketType) => {
  if (marketType === 1 || marketType === 2 || marketType === 3) {
    return STOCK_QUOTE_TYPE.TIME_SHARE;
  }
  return STOCK_QUOTE_TYPE.DAILY_K;
};
